import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import DreamCard from '@/components/DreamCard';
import GoldenDreamCard from '@/components/GoldenDreamCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Class-grouped dreams for anonymous users — age-appropriate content
const ANON_STARTER_DREAMS = {
  // Classes 1-2 (age 6-7): simple, tangible, everyday wonder
  '1': [
    { id: 'anon_1_1', dream_hook: 'Your pet dreamed about counting all the butterflies in a garden...', emoji: '🦋', subject: 'Mathematics', topic: 'Counting' },
    { id: 'anon_1_2', dream_hook: 'Your pet dreamed about a rainbow that had only 3 colors...', emoji: '🌈', subject: 'Science', topic: 'Colors' },
    { id: 'anon_1_3', dream_hook: 'Your pet dreamed about letters dancing and making new words...', emoji: '✏️', subject: 'English', topic: 'Letters and Words' },
    { id: 'anon_1_4', dream_hook: 'Your pet dreamed about a tiny ant carrying a huge leaf...', emoji: '🐜', subject: 'Science', topic: 'Animals Around Us' },
    { id: 'anon_1_5', dream_hook: 'Your pet dreamed about sharing 10 laddoos equally with friends...', emoji: '🍬', subject: 'Mathematics', topic: 'Sharing Equally' },
  ],
  '2': [
    { id: 'anon_2_1', dream_hook: 'Your pet dreamed about a plant that grew taller than the house...', emoji: '🌱', subject: 'Science', topic: 'Plants Around Us' },
    { id: 'anon_2_2', dream_hook: 'Your pet dreamed about telling time when the clock had no numbers...', emoji: '🕐', subject: 'Mathematics', topic: 'Time' },
    { id: 'anon_2_3', dream_hook: 'Your pet dreamed about animals that only come out at night...', emoji: '🦉', subject: 'Science', topic: 'Day and Night Animals' },
    { id: 'anon_2_4', dream_hook: 'Your pet dreamed about a storybook where the pictures moved...', emoji: '📖', subject: 'English', topic: 'Stories' },
    { id: 'anon_2_5', dream_hook: 'Your pet dreamed about finding shapes in everything around the house...', emoji: '🔶', subject: 'Mathematics', topic: 'Shapes' },
  ],
  // Classes 3-4 (age 8-9): curiosity, why questions, Indian context
  '3': [
    { id: 'anon_3_1', dream_hook: 'Your pet dreamed about where rain goes after it falls on the ground...', emoji: '💧', subject: 'Science', topic: 'Water' },
    { id: 'anon_3_2', dream_hook: 'Your pet dreamed about a shop where everything costs exactly ₹10...', emoji: '🏪', subject: 'Mathematics', topic: 'Money' },
    { id: 'anon_3_3', dream_hook: 'Your pet dreamed about how birds find their way home...', emoji: '🐦', subject: 'Science', topic: 'Birds' },
    { id: 'anon_3_4', dream_hook: 'Your pet dreamed about a letter that traveled through many hands to reach someone...', emoji: '✉️', subject: 'English', topic: 'Communication' },
    { id: 'anon_3_5', dream_hook: 'Your pet dreamed about measuring the playground using only footsteps...', emoji: '👣', subject: 'Mathematics', topic: 'Measurement' },
  ],
  '4': [
    { id: 'anon_4_1', dream_hook: 'Your pet dreamed about a seed that refused to grow without sunlight...', emoji: '🌻', subject: 'Science', topic: 'Plants and Sunlight' },
    { id: 'anon_4_2', dream_hook: 'Your pet dreamed about sharing a pizza equally among 4 friends...', emoji: '🍕', subject: 'Mathematics', topic: 'Fractions' },
    { id: 'anon_4_3', dream_hook: 'Your pet dreamed about what happens inside the earth during an earthquake...', emoji: '🌍', subject: 'Science', topic: 'Earth' },
    { id: 'anon_4_4', dream_hook: 'Your pet dreamed about a poem that rhymed in a funny way...', emoji: '📝', subject: 'English', topic: 'Poetry' },
    { id: 'anon_4_5', dream_hook: 'Your pet dreamed about maps that show treasure buried underground...', emoji: '🗺️', subject: 'Social Studies', topic: 'Maps' },
  ],
  // Classes 5-6 (age 10-11): deeper reasoning, experiments, Indian culture
  '5': [
    { id: 'anon_5_1', dream_hook: 'Your pet dreamed about water droplets going on a round trip to the clouds...', emoji: '💧', subject: 'Science', topic: 'Water Cycle' },
    { id: 'anon_5_2', dream_hook: 'Your pet dreamed about a cricket bat that could measure its own length...', emoji: '🏏', subject: 'Mathematics', topic: 'Measurement' },
    { id: 'anon_5_3', dream_hook: 'Your pet dreamed about magnets that made friends with iron nails...', emoji: '🧲', subject: 'Science', topic: 'Magnets' },
    { id: 'anon_5_4', dream_hook: 'Your pet dreamed about ancient kings building forts taller than mountains...', emoji: '🏰', subject: 'History', topic: 'Ancient India' },
    { id: 'anon_5_5', dream_hook: 'Your pet dreamed about a word that sounded the same but meant two things...', emoji: '📖', subject: 'English', topic: 'Homophones' },
  ],
  '6': [
    { id: 'anon_6_1', dream_hook: 'Your pet dreamed about a seed that became a forest over 100 years...', emoji: '🌳', subject: 'Science', topic: 'Photosynthesis' },
    { id: 'anon_6_2', dream_hook: 'Your pet dreamed about shapes hiding inside a rangoli pattern...', emoji: '🔷', subject: 'Mathematics', topic: 'Geometry' },
    { id: 'anon_6_3', dream_hook: 'Your pet dreamed about a river that carved a valley over a thousand years...', emoji: '🏞️', subject: 'Geography', topic: 'Erosion' },
    { id: 'anon_6_4', dream_hook: 'Your pet dreamed about mixing two colors and getting a surprise third one...', emoji: '🎨', subject: 'Science', topic: 'Color Mixing' },
    { id: 'anon_6_5', dream_hook: 'Your pet dreamed about counting all the mangoes in a giant orchard...', emoji: '🥭', subject: 'Mathematics', topic: 'Multiplication' },
  ],
  // Classes 7-8 (age 12-14): abstract thinking, real-world applications
  '7': [
    { id: 'anon_7_1', dream_hook: 'Your pet dreamed about atoms playing musical chairs inside a molecule...', emoji: '⚛️', subject: 'Chemistry', topic: 'Atomic Structure' },
    { id: 'anon_7_2', dream_hook: 'Your pet dreamed about x and y having a secret meeting on a graph...', emoji: '📊', subject: 'Mathematics', topic: 'Algebra' },
    { id: 'anon_7_3', dream_hook: 'Your pet dreamed about a village that voted for its own rules...', emoji: '🗳️', subject: 'Civics', topic: 'Democracy' },
    { id: 'anon_7_4', dream_hook: 'Your pet dreamed about light bending when it enters water...', emoji: '🔦', subject: 'Physics', topic: 'Refraction' },
    { id: 'anon_7_5', dream_hook: 'Your pet dreamed about a poet who turned rebellion into beautiful words...', emoji: '✍️', subject: 'English', topic: 'Poetry' },
  ],
  '8': [
    { id: 'anon_8_1', dream_hook: 'Your pet dreamed about tectonic plates having a slow-motion dance battle...', emoji: '🌍', subject: 'Geography', topic: 'Plate Tectonics' },
    { id: 'anon_8_2', dream_hook: 'Your pet dreamed about equations that balance like a see-saw...', emoji: '⚖️', subject: 'Mathematics', topic: 'Linear Equations' },
    { id: 'anon_8_3', dream_hook: 'Your pet dreamed about cells dividing to build a whole human body...', emoji: '🧬', subject: 'Biology', topic: 'Cell Division' },
    { id: 'anon_8_4', dream_hook: 'Your pet dreamed about the freedom fighters who changed India forever...', emoji: '🇮🇳', subject: 'History', topic: 'Indian Independence' },
    { id: 'anon_8_5', dream_hook: 'Your pet dreamed about sound waves bouncing off mountains as echoes...', emoji: '🔊', subject: 'Physics', topic: 'Sound' },
  ],
};

function shuffleAndPick(arr, n) {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

export default function DreamSection({ user, onGenerateStory }) {
  const [dreams, setDreams] = useState([]);
  const [goldenDream, setGoldenDream] = useState(null);
  const [dreamDate, setDreamDate] = useState('');
  const [exploredIds, setExploredIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const petName = localStorage.getItem('you_pet_name') || 'Your pet';
  const email = localStorage.getItem('you_user_email') || '';
  const board = localStorage.getItem('you_board') || 'cbse';
  const classRaw = localStorage.getItem('you_class') || 'class_5_6';
  const classNum = localStorage.getItem('you_class_number')
    || { class_1_2:'1', class_3_4:'3', class_5_6:'5', class_7_8:'7' }[classRaw]
    || '5';
  const cls = classNum;

  const fetchDreams = useCallback(async () => {
    setLoading(true);
    setError(false);

    if (!email) {
      // Anonymous: use class-appropriate starter dreams
      const classDreams = ANON_STARTER_DREAMS[classNum] || ANON_STARTER_DREAMS['5'];
      setDreams(shuffleAndPick(classDreams, 3));
      setDreamDate(new Date().toISOString().split('T')[0]);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API}/daily-dreams?user_email=${encodeURIComponent(email)}&board=${encodeURIComponent(board)}&class=${encodeURIComponent(cls)}`
      );
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setDreams(data.dreams || []);
      setGoldenDream(data.golden_dream || null);
      setDreamDate(data.dream_date || new Date().toISOString().split('T')[0]);
    } catch {
      setError(true);
      // Fallback to class-appropriate anon dreams so the child always sees something
      const classDreams = ANON_STARTER_DREAMS[classNum] || ANON_STARTER_DREAMS['5'];
      setDreams(shuffleAndPick(classDreams, 3));
      setDreamDate(new Date().toISOString().split('T')[0]);
    } finally {
      setLoading(false);
    }
  }, [email, board, cls, classNum]);

  useEffect(() => { fetchDreams(); }, [fetchDreams]);

  const handleExplore = useCallback((dream) => {
    // 1. Mark explored locally
    setExploredIds(prev => new Set([...prev, dream.id]));

    // 2. Store for Celebration.jsx to update diary score later
    localStorage.setItem('you_current_dream_id', dream.id);
    localStorage.setItem('you_current_dream_date', dreamDate);

    // 3. Log to backend (non-blocking)
    if (email) {
      fetch(`${API}/explore-dream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: email,
          dream_id: dream.id,
          dream_date: dreamDate,
          topic_ref: (dream.subject || '') + '/' + (dream.topic || dream.topic_id || ''),
          dream_hook: dream.dream_hook || '',
        }),
      }).catch(() => {});
    }

    // 4. Trigger story generation
    onGenerateStory(dream.topic || dream.dream_hook);
  }, [email, dreamDate, onGenerateStory]);

  const allExplored = dreams.length > 0 && dreams.every(d => exploredIds.has(d.id));

  return (
    <div style={{ width: '100%' }}>
      {/* Section header */}
      <div style={{
        background: 'linear-gradient(180deg, rgba(237,231,246,0.5), transparent)',
        borderRadius: 4, padding: 16,
        border: '1.5px dashed rgba(124,77,255,0.2)',
        marginBottom: 12,
      }}>
        <h2 style={{
          fontSize: 22, fontFamily: "'Patrick Hand', cursive", fontWeight: 400,
          color: '#5E35B1', margin: 0,
        }}>
          {petName}'s Dreams 🌙
        </h2>
        <p style={{
          fontSize: 14, fontFamily: "'Inter', sans-serif", fontWeight: 400,
          color: '#7E57C2', margin: '4px 0 0',
        }}>
          tap a dream to explore with {petName}!
        </p>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[0, 1, 2].map(i => (
            <motion.div
              key={i}
              animate={{ opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              style={{
                background: '#FFFFFF', border: '1.5px dashed #DDDDDD',
                borderRadius: 4, height: 100, padding: 16,
              }}
            />
          ))}
          <p style={{
            fontSize: 16, fontFamily: "'Patrick Hand', cursive",
            color: '#B39DDB', textAlign: 'center', margin: '8px 0 0',
          }}>
            {petName} is dreaming...
          </p>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div style={{
          textAlign: 'center', padding: '20px 16px',
          border: '1.5px dashed #DDDDDD', borderRadius: 4,
          background: '#FFFFFF',
        }}>
          <p style={{
            fontSize: 16, fontFamily: "'Patrick Hand', cursive",
            color: '#1A1A2E', margin: '0 0 12px',
          }}>
            {petName}'s dreams are sleeping... 😴
          </p>
          <button
            onClick={fetchDreams}
            style={{
              fontSize: 14, fontFamily: "'Patrick Hand', cursive",
              padding: '8px 20px', borderRadius: 20,
              border: 'none', background: '#448AFF', color: '#FFFFFF',
              cursor: 'pointer', minHeight: 40,
            }}
          >
            Wake {petName} up! 🌅
          </button>
        </div>
      )}

      {/* Dream cards */}
      {!loading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {dreams.map((dream, i) => (
            <DreamCard
              key={dream.id}
              dream={dream}
              index={i}
              onExplore={handleExplore}
              isExplored={exploredIds.has(dream.id)}
              petName={petName}
            />
          ))}

          {goldenDream && (
            <GoldenDreamCard
              dream={goldenDream}
              onExplore={handleExplore}
              isExplored={exploredIds.has(goldenDream.id)}
              petName={petName}
            />
          )}

          {/* All explored */}
          {allExplored && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              style={{
                textAlign: 'center', padding: '20px 16px',
                border: '1.5px dashed rgba(124,77,255,0.2)',
                borderRadius: 4, background: 'rgba(237,231,246,0.3)',
                marginTop: 4,
              }}
            >
              <p style={{
                fontSize: 16, fontFamily: "'Patrick Hand', cursive",
                color: '#5E35B1', margin: '0 0 4px',
              }}>
                You explored all of {petName}'s dreams today! 🎉
              </p>
              <p style={{
                fontSize: 14, fontFamily: "'Inter', sans-serif", fontWeight: 400,
                color: '#7E57C2', margin: 0,
              }}>
                {petName} needs to sleep to dream more! Come back tomorrow 🌙
              </p>
            </motion.div>
          )}

          {/* Diary link */}
          {email && exploredIds.size > 0 && (
            <p style={{
              fontSize: 14, fontFamily: "'Patrick Hand', cursive",
              color: '#7E57C2', textAlign: 'center', margin: '12px 0 0',
            }}>
              See your dream diary in the Stories tab
            </p>
          )}
        </div>
      )}
    </div>
  );
}
